// Admin → Photo Audit
//
// Read-only diagnostic tool that scans every field-report form (PreCon /
// Locate / Soft Dig) and compares the photo records saved in MongoDB
// against the actual files in each form's OneDrive folder.
//
// Surfaces "orphan" files — photos that exist on OneDrive but were never
// (or were no longer) recorded in the DB photo array. Those are the lost
// records caused by the multi-photo upload stale-closure bug fixed
// 2026-06-12.
//
// NO WRITES. Calls: GET /api/admin/photo-audit/scan?form_type=...

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  Camera,
  Download,
  Search,
  FolderOpen,
  ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const FORM_TYPES = [
  { value: 'all',       label: 'All field-report forms' },
  { value: 'precons',   label: 'PreCon (Site Scouting) only' },
  { value: 'locates',   label: 'Locate only' },
  { value: 'soft-digs', label: 'Soft Dig only' },
];

export default function AdminPhotoAudit() {
  const [scope, setScope] = useState('all');
  const [scanning, setScanning] = useState(false);
  const [report, setReport] = useState(null);

  // Signature URL diagnostic state — read-only call against the 4 affected
  // PreCons from the 2026-06-13 incident. No writes. Surfaces what each
  // form's submitter_signature_url / reviewer_signature_url field actually
  // contains so the admin can confirm before any cleanup.
  const [sigDiagLoading, setSigDiagLoading] = useState(false);
  const [sigDiagReport,  setSigDiagReport]  = useState(null);
  const [sigDiagError,   setSigDiagError]   = useState(null);
  const SIG_DIAG_JOB_NUMBERS = [
    'J20260506-004',
    'J20260507-001',
    'J20260507-002',
    'J20260513-007',
  ];

  // Deletion-overlap check state — read-only intersection of recent
  // orphan_signature_cleanup deletions with the live signature URLs on
  // the same 4 forms. Tells the admin definitively whether any restore
  // from OneDrive Recycle Bin is needed.
  const [overlapLoading, setOverlapLoading] = useState(false);
  const [overlapReport,  setOverlapReport]  = useState(null);
  const [overlapError,   setOverlapError]   = useState(null);

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('fieldflow_token')}` },
  });

  // Calls the existing GET /api/admin/photo-audit/signature-url-diagnose
  // for the 4 PreCons from the 2026-06-13 incident. Read-only; backend
  // writes nothing. Results render in a small table below.
  const runSignatureDiagnostic = async () => {
    setSigDiagLoading(true);
    setSigDiagError(null);
    setSigDiagReport(null);
    try {
      const r = await axios.get(
        `${API}/admin/photo-audit/signature-url-diagnose`,
        {
          ...authHeaders(),
          params: { job_numbers: SIG_DIAG_JOB_NUMBERS.join(',') },
        }
      );
      setSigDiagReport(r.data);
      const t = r.data.summary;
      toast.info(
        `Diagnosed ${t.forms_returned}/${SIG_DIAG_JOB_NUMBERS.length} forms · ` +
        `${t.onedrive_ids_referenced.length} OneDrive ID(s) referenced`
      );
    } catch (e) {
      setSigDiagError(e?.response?.data?.detail || e.message);
      toast.error(`Diagnostic failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSigDiagLoading(false);
    }
  };

  // Reads photo_deletion_log for recent orphan_signature_cleanup entries
  // and cross-checks their OneDrive IDs against the 4 forms' current
  // signature URLs. Pure read; no writes; backend computes the intersection.
  const runOverlapCheck = async () => {
    setOverlapLoading(true);
    setOverlapError(null);
    setOverlapReport(null);
    try {
      const r = await axios.get(
        `${API}/admin/photo-audit/deletion-overlap-check`,
        {
          ...authHeaders(),
          params: {
            job_numbers: SIG_DIAG_JOB_NUMBERS.join(','),
            within_hours: 24,
          },
        }
      );
      setOverlapReport(r.data);
      if (r.data.overlap_count > 0) {
        toast.error(
          `⚠ ${r.data.overlap_count} deleted file(s) STILL REFERENCED — restore required.`
        );
      } else {
        toast.success(
          `No overlap. ${r.data.recently_deleted_oids.length} recent deletes vs. ` +
          `${r.data.live_signature_oids.length} live refs. No restore needed.`
        );
      }
    } catch (e) {
      setOverlapError(e?.response?.data?.detail || e.message);
      toast.error(`Overlap check failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setOverlapLoading(false);
    }
  };

  const runScan = async () => {
    setScanning(true);
    setReport(null);
    try {
      const r = await axios.get(
        `${API}/admin/photo-audit/scan`,
        { ...authHeaders(), params: { form_type: scope, only_affected: true } }
      );
      setReport(r.data);
      const t = r.data.totals;
      if (t.forms_with_orphans === 0) {
        toast.success(`Scan complete: ${t.forms_scanned} forms checked. None affected.`);
      } else {
        toast.warning(`Scan complete: ${t.forms_with_orphans} forms have orphaned OneDrive photos.`);
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error(`Scan failed: ${msg}`);
    } finally {
      setScanning(false);
    }
  };

  const downloadCsv = () => {
    if (!report || !report.forms?.length) return;
    const rows = [
      ['form_type', 'job_number', 'form_id', 'status', 'db_photos', 'onedrive_photos',
       'orphan_count', 'dangling_count', 'folder_status', 'created_at'],
      ...report.forms.map(f => [
        f.form_type, f.job_number || '', f.form_id, f.status_field || '',
        f.db_photo_count, f.onedrive_photo_count, f.orphan_count, f.dangling_count,
        f.folder_status, f.created_at || '',
      ]),
    ];
    const csv = rows.map(r => r.map(c => {
      const s = String(c ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(',')).join('\n');
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `photo-audit-${stamp}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="container mx-auto py-6 px-4 max-w-6xl space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Camera className="w-6 h-6 text-blue-600" />
            Photo Audit
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Compare each field-report form&apos;s saved photo records against the actual
            files in its OneDrive folder. Surfaces &quot;orphan&quot; files — photos in OneDrive
            that are missing from the database (the symptom of the multi-photo upload
            bug fixed on 2026-06-12).
          </p>
        </div>
        <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-700 px-3 py-1">
          <ShieldCheck className="w-3 h-3 mr-1" />
          Read-only — no writes
        </Badge>
      </div>

      <div className="flex justify-end -mt-2">
        <Link
          to="/admin/photo-history"
          className="text-sm text-blue-600 hover:underline"
          data-testid="open-photo-history"
        >
          View photo history (recoveries + deletions) →
        </Link>
      </div>

      {/* Scan controls */}
      <Card data-testid="photo-audit-controls">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Search className="w-4 h-4" />
            Run Scan
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-sm font-medium text-slate-700">Scope:</label>
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              disabled={scanning}
              className="border border-slate-300 rounded-md px-3 py-2 text-sm bg-white disabled:opacity-50"
              data-testid="photo-audit-scope"
            >
              {FORM_TYPES.map(f => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
            <Button
              onClick={runScan}
              disabled={scanning}
              data-testid="photo-audit-scan-btn"
              className="bg-blue-600 hover:bg-blue-700"
            >
              {scanning ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Scanning…</>
              ) : (
                <><Search className="w-4 h-4 mr-2" /> Scan Now</>
              )}
            </Button>
            {report && (
              <Button
                onClick={downloadCsv}
                variant="outline"
                disabled={!report.forms?.length}
                data-testid="photo-audit-csv-btn"
              >
                <Download className="w-4 h-4 mr-2" /> Download CSV
              </Button>
            )}
            <Button
              onClick={runSignatureDiagnostic}
              disabled={sigDiagLoading}
              variant="outline"
              data-testid="signature-diagnose-btn"
            >
              {sigDiagLoading
                ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Diagnosing…</>
                : <><ShieldCheck className="w-4 h-4 mr-2" />Diagnose signatures</>}
            </Button>
            <Button
              onClick={runOverlapCheck}
              disabled={overlapLoading}
              variant="outline"
              data-testid="overlap-check-btn"
              className="border-red-300 text-red-700 hover:bg-red-50"
            >
              {overlapLoading
                ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Checking…</>
                : <><AlertTriangle className="w-4 h-4 mr-2" />Check deletion overlap</>}
            </Button>
          </div>
          <p className="text-xs text-slate-500">
            Scans every form sequentially against the Microsoft Graph API. Cap of
            2,000 forms per type per scan. A full all-types scan typically takes
            10–60 seconds depending on form count.
          </p>
        </CardContent>
      </Card>

      {/* Empty state */}
      {!report && !scanning && (
        <Card>
          <CardContent className="py-12 text-center text-slate-500">
            <FolderOpen className="w-10 h-10 mx-auto text-slate-300 mb-3" />
            <p className="text-sm">No scan results yet. Choose a scope above and click <strong>Scan Now</strong>.</p>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {report && <ResultsView report={report} />}

      {/* Signature URL diagnostic results */}
      {sigDiagError && (
        <Card className="border-red-200" data-testid="signature-diagnose-error">
          <CardContent className="pt-6 text-red-700 text-sm">
            Diagnostic failed: {sigDiagError}
          </CardContent>
        </Card>
      )}
      {sigDiagReport && (
        <Card className="border-slate-300" data-testid="signature-diagnose-results">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-emerald-600" />
              Signature URL diagnostic
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-slate-500">
              Checked at {new Date(sigDiagReport.checked_at).toLocaleString()}.
              {' '}{sigDiagReport.summary.forms_returned} of {SIG_DIAG_JOB_NUMBERS.length} forms found.
            </p>

            {sigDiagReport.summary.missing.length > 0 && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                Not found in the database:{' '}
                {sigDiagReport.summary.missing.map(m => m.asked).join(', ')}
              </div>
            )}

            <div className="border border-slate-200 rounded overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 text-slate-600">
                  <tr>
                    <th className="text-left p-2">Job #</th>
                    <th className="text-left p-2">Status</th>
                    <th className="text-left p-2">Submitter signature</th>
                    <th className="text-left p-2">Reviewer signature</th>
                    <th className="text-left p-2">OneDrive ID(s) referenced</th>
                  </tr>
                </thead>
                <tbody>
                  {sigDiagReport.forms.map(f => {
                    const shapeBadge = (shape) => {
                      const colors = {
                        onedrive:    'bg-blue-100 text-blue-800',
                        cloudinary:  'bg-purple-100 text-purple-800',
                        base64:      'bg-amber-100 text-amber-800',
                        null:        'bg-slate-100 text-slate-500',
                        other:       'bg-rose-100 text-rose-800',
                        non_string:  'bg-rose-100 text-rose-800',
                      };
                      return (
                        <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${colors[shape] || ''}`}>
                          {shape}
                        </span>
                      );
                    };
                    const oidsHere = [
                      f.submitter.url_field?.onedrive_id,
                      f.reviewer.url_field?.onedrive_id,
                    ].filter(Boolean);
                    return (
                      <tr key={f.form_id} className="border-t border-slate-200 align-top">
                        <td className="p-2 font-bold">{f.job_number}</td>
                        <td className="p-2 text-slate-500">{f.status || '—'}</td>
                        <td className="p-2">
                          {shapeBadge(f.submitter.url_field.shape)}
                          {f.submitter.url_field.cloudinary_public_id && (
                            <div className="text-[10px] text-slate-500 font-mono mt-1 break-all">
                              {f.submitter.url_field.cloudinary_public_id}
                            </div>
                          )}
                          {f.submitter.url_field.shape === 'base64' && (
                            <div className="text-[10px] text-slate-500 mt-1">
                              inline · {f.submitter.url_field.length} chars
                            </div>
                          )}
                        </td>
                        <td className="p-2">
                          {shapeBadge(f.reviewer.url_field.shape)}
                          {f.reviewer.url_field.cloudinary_public_id && (
                            <div className="text-[10px] text-slate-500 font-mono mt-1 break-all">
                              {f.reviewer.url_field.cloudinary_public_id}
                            </div>
                          )}
                          {f.reviewer.url_field.shape === 'base64' && (
                            <div className="text-[10px] text-slate-500 mt-1">
                              inline · {f.reviewer.url_field.length} chars
                            </div>
                          )}
                        </td>
                        <td className="p-2 font-mono text-[10px] text-slate-700 break-all">
                          {oidsHere.length ? oidsHere.join(', ') : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {sigDiagReport.summary.onedrive_ids_referenced.length > 0 && (
              <div className="text-xs bg-blue-50 border border-blue-200 rounded p-3">
                <p className="font-semibold text-blue-900 mb-1">
                  Live OneDrive references across these 4 forms:
                </p>
                <p className="font-mono text-blue-800 break-all">
                  {sigDiagReport.summary.onedrive_ids_referenced.join(', ')}
                </p>
                <p className="text-blue-700 mt-2">
                  These are the live signature references — none of these
                  IDs should ever be deleted from OneDrive.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Deletion overlap check */}
      {overlapError && (
        <Card className="border-red-200" data-testid="overlap-check-error">
          <CardContent className="pt-6 text-red-700 text-sm">
            Overlap check failed: {overlapError}
          </CardContent>
        </Card>
      )}
      {overlapReport && (
        <Card
          className={overlapReport.overlap_count > 0
            ? "border-red-500 border-2 bg-red-50"
            : "border-emerald-300 bg-emerald-50"}
          data-testid="overlap-check-results"
        >
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              {overlapReport.overlap_count > 0 ? (
                <AlertTriangle className="w-6 h-6 text-red-700" />
              ) : (
                <ShieldCheck className="w-6 h-6 text-emerald-700" />
              )}
              <span className={overlapReport.overlap_count > 0 ? "text-red-900" : "text-emerald-900"}>
                Deletion overlap check —
                {' '}
                {overlapReport.overlap_count > 0
                  ? `${overlapReport.overlap_count} FILE(S) STILL REFERENCED`
                  : 'CLEAN'}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <p className={overlapReport.overlap_count > 0 ? "text-red-800 font-semibold" : "text-emerald-800"}>
              {overlapReport.verdict}
            </p>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="bg-white rounded p-2 border">
                <p className="text-slate-500 mb-1">
                  Live signature OneDrive IDs ({overlapReport.live_signature_oids.length}):
                </p>
                <p className="font-mono break-all">
                  {overlapReport.live_signature_oids.join(', ') || '(none)'}
                </p>
              </div>
              <div className="bg-white rounded p-2 border">
                <p className="text-slate-500 mb-1">
                  Recently deleted OneDrive IDs ({overlapReport.recently_deleted_oids.length} in last {overlapReport.window_hours}h):
                </p>
                <p className="font-mono break-all">
                  {overlapReport.recently_deleted_oids.join(', ') || '(none)'}
                </p>
              </div>
            </div>

            {overlapReport.overlap_count > 0 && (
              <div className="border-2 border-red-600 rounded p-3 bg-white">
                <p className="font-bold text-red-800 text-sm mb-2">
                  🚨 RESTORE THESE FILES FROM ONEDRIVE RECYCLE BIN IMMEDIATELY:
                </p>
                <table className="w-full text-xs">
                  <thead className="bg-red-100 text-red-900">
                    <tr>
                      <th className="text-left p-2">filename</th>
                      <th className="text-left p-2">OneDrive ID</th>
                      <th className="text-left p-2">form (job #)</th>
                      <th className="text-left p-2">field</th>
                      <th className="text-left p-2">deleted at</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overlapReport.overlap_detail.map((d, i) => (
                      <tr key={i} className="border-t border-red-200">
                        <td className="p-2 font-mono">{d.filename || '—'}</td>
                        <td className="p-2 font-mono break-all">{d.onedrive_id}</td>
                        <td className="p-2 font-bold">{d.now_referenced_on_form}</td>
                        <td className="p-2">{d.now_referenced_by}</td>
                        <td className="p-2 text-slate-600">
                          {d.deleted_at ? new Date(d.deleted_at).toLocaleString() : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <p className="text-[10px] text-slate-500">
              Checked at {new Date(overlapReport.checked_at).toLocaleString()} ·
              window: last {overlapReport.window_hours} hours ·
              jobs examined: {overlapReport.job_numbers_examined.join(', ')}
              {overlapReport.job_numbers_missing_in_db.length > 0 && (
                <> · missing in DB: {overlapReport.job_numbers_missing_in_db.join(', ')}</>
              )}
            </p>
          </CardContent>
        </Card>
      )}

    </div>
  );
}


function ResultsView({ report }) {
  const t = report.totals;
  const stamp = new Date(report.scanned_at).toLocaleString();
  const allClear = t.forms_with_orphans === 0 && t.forms_with_list_errors === 0;

  // Sort forms — already pre-sorted by orphan_count desc on the backend.
  const forms = report.forms || [];

  return (
    <>
      {/* Summary */}
      <Card data-testid="photo-audit-summary">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            {allClear ? (
              <><CheckCircle2 className="w-4 h-4 text-emerald-600" /> All clear</>
            ) : (
              <><AlertTriangle className="w-4 h-4 text-amber-600" /> Forms with missing photo records</>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Forms scanned"          value={t.forms_scanned}          tone="slate"  testid="stat-forms-scanned" />
            <Stat label="Forms with orphans"     value={t.forms_with_orphans}     tone={t.forms_with_orphans ? 'amber' : 'emerald'} testid="stat-forms-with-orphans" />
            <Stat label="Total orphan files"     value={t.total_orphan_files}     tone={t.total_orphan_files ? 'amber' : 'emerald'} testid="stat-total-orphans" />
            <Stat label="Forms w/ list errors"   value={t.forms_with_list_errors} tone={t.forms_with_list_errors ? 'red' : 'slate'} testid="stat-list-errors" />
            <Stat label="Total DB photos"        value={t.total_db_photos}        tone="slate" />
            <Stat label="Total OneDrive photos"  value={t.total_onedrive_photos}  tone="slate" />
            <Stat label="No OneDrive folder"     value={t.forms_with_no_onedrive_folder} tone="slate" />
          </div>
          <p className="text-xs text-slate-500">
            Scanned at <span className="font-mono">{stamp}</span> · scope: {report.scoped_to.join(', ')}
          </p>
        </CardContent>
      </Card>

      {/* Per-form-type breakdown */}
      {report.by_form_type && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">By form type</CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm" data-testid="photo-audit-by-type">
              <thead>
                <tr className="border-b border-slate-200 text-slate-500 text-left">
                  <th className="py-2 pr-3 font-medium">Form type</th>
                  <th className="py-2 pr-3 font-medium text-right">Scanned</th>
                  <th className="py-2 pr-3 font-medium text-right">With orphans</th>
                  <th className="py-2 pr-3 font-medium text-right">DB photos</th>
                  <th className="py-2 pr-3 font-medium text-right">OneDrive photos</th>
                  <th className="py-2 pr-3 font-medium text-right">Orphan files</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(report.by_form_type).map(([ft, s]) => (
                  <tr key={ft} className="border-b border-slate-100">
                    <td className="py-2 pr-3 font-medium text-slate-700">{ft}</td>
                    <td className="py-2 pr-3 text-right">{s.forms_scanned}</td>
                    <td className={`py-2 pr-3 text-right ${s.forms_with_orphans ? 'text-amber-700 font-semibold' : ''}`}>
                      {s.forms_with_orphans}
                    </td>
                    <td className="py-2 pr-3 text-right text-slate-600">{s.total_db_photos}</td>
                    <td className="py-2 pr-3 text-right text-slate-600">{s.total_onedrive_photos}</td>
                    <td className={`py-2 pr-3 text-right ${s.total_orphan_files ? 'text-amber-700 font-semibold' : ''}`}>
                      {s.total_orphan_files}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Affected forms list */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Affected forms ({forms.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {forms.length === 0 ? (
            <p className="text-sm text-slate-500 py-4">
              No affected forms found. Every photo on OneDrive has a matching record in the database.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="photo-audit-forms-table">
                <thead>
                  <tr className="border-b border-slate-200 text-slate-500 text-left">
                    <th className="py-2 pr-3 font-medium">Form type</th>
                    <th className="py-2 pr-3 font-medium">Job #</th>
                    <th className="py-2 pr-3 font-medium">Status</th>
                    <th className="py-2 pr-3 font-medium text-right">DB</th>
                    <th className="py-2 pr-3 font-medium text-right">OneDrive</th>
                    <th className="py-2 pr-3 font-medium text-right">Orphans</th>
                    <th className="py-2 pr-3 font-medium text-right">Dangling</th>
                    <th className="py-2 pr-3 font-medium">Folder</th>
                    <th className="py-2 pr-3 font-medium text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {forms.map(f => <FormRow key={`${f.form_type}-${f.form_id}`} f={f} />)}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}


function FormRow({ f }) {
  const [open, setOpen] = useState(false);
  const tone =
    f.folder_status === 'error' ? 'text-red-600' :
    f.folder_status === 'no_folder' ? 'text-slate-400' :
    'text-slate-600';

  return (
    <>
      <tr className="border-b border-slate-100 hover:bg-slate-50">
        <td className="py-2 pr-3 font-medium text-slate-700">{f.form_type}</td>
        <td className="py-2 pr-3 font-mono text-xs">
          <button
            className="text-blue-600 hover:underline"
            onClick={() => setOpen(o => !o)}
            data-testid={`form-row-toggle-${f.form_id}`}
          >
            {f.job_number || '(unnamed)'}
          </button>
        </td>
        <td className="py-2 pr-3 text-xs text-slate-500">{f.status_field || '—'}</td>
        <td className="py-2 pr-3 text-right">{f.db_photo_count}</td>
        <td className="py-2 pr-3 text-right">{f.onedrive_photo_count}</td>
        <td className={`py-2 pr-3 text-right font-semibold ${f.orphan_count ? 'text-amber-700' : 'text-slate-400'}`}>
          {f.orphan_count}
        </td>
        <td className={`py-2 pr-3 text-right ${f.dangling_count ? 'text-red-600' : 'text-slate-400'}`}>
          {f.dangling_count}
        </td>
        <td className={`py-2 pr-3 text-xs ${tone}`}>
          {f.folder_status === 'ok' ? 'OK' : f.folder_status}
          {f.folder_error && <span className="ml-1 text-red-500">({f.folder_error})</span>}
        </td>
        <td className="py-2 pr-3 text-right">
          {f.orphan_count > 0 ? (
            <Link
              to={`/admin/photo-audit/recover/${f.form_type}/${f.form_id}`}
              className="inline-flex items-center px-2 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded"
              data-testid={`recover-btn-${f.form_id}`}
            >
              Recover →
            </Link>
          ) : (
            <span className="text-xs text-slate-400">—</span>
          )}
        </td>
      </tr>
      {open && (
        <tr className="bg-slate-50/60">
          <td colSpan={9} className="py-3 px-3">
            <div className="space-y-2">
              <p className="text-xs text-slate-600 font-medium">
                Form ID: <span className="font-mono">{f.form_id}</span> ·
                {' '}Orphaned OneDrive files (chronological by upload time):
              </p>
              {f.orphans.length === 0 ? (
                <p className="text-xs text-slate-500 italic">No orphaned files. Discrepancy may be dangling DB records.</p>
              ) : (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-slate-500">
                      <th className="text-left py-1 pr-2 font-medium">Filename</th>
                      <th className="text-left py-1 pr-2 font-medium">Created (OneDrive)</th>
                      <th className="text-right py-1 pr-2 font-medium">Size</th>
                      <th className="text-left py-1 pr-2 font-medium">Web link</th>
                    </tr>
                  </thead>
                  <tbody>
                    {f.orphans.map(o => (
                      <tr key={o.onedrive_id} className="border-t border-slate-200">
                        <td className="py-1 pr-2 font-mono">{o.name}</td>
                        <td className="py-1 pr-2">{o.created ? new Date(o.created).toLocaleString() : '—'}</td>
                        <td className="py-1 pr-2 text-right">{o.size != null ? `${(o.size / 1024).toFixed(0)} KB` : '—'}</td>
                        <td className="py-1 pr-2">
                          {o.webUrl ? (
                            <a href={o.webUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">open ↗</a>
                          ) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}


function Stat({ label, value, tone = 'slate', testid }) {
  const toneClasses = {
    slate:   'bg-slate-50 text-slate-900 border-slate-200',
    amber:   'bg-amber-50 text-amber-900 border-amber-200',
    emerald: 'bg-emerald-50 text-emerald-900 border-emerald-200',
    red:     'bg-red-50 text-red-900 border-red-200',
  }[tone];
  return (
    <div
      className={`rounded-md border px-3 py-2 ${toneClasses}`}
      data-testid={testid}
    >
      <div className="text-xs uppercase tracking-wide font-medium opacity-70">{label}</div>
      <div className="text-2xl font-bold tabular-nums">{value}</div>
    </div>
  );
}
